import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useDebugStore } from '@client/stores/DebugStore';
import { trpcOptions } from '@client/trpc/client';
import { getFeatureFlagReadiness, subscribeFeatureFlagReadiness } from '@client/utils/feature-flags';
import { matchQuery, type QueryFilters, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { readIsUIDebugInfoEnabledSync, UI_DEBUG_INFO_FLAG_KEY } from './useIsUIDebugInfoEnabled';

/**
 * The query procedures whose response depends on `x-debug-info`: every tRPC
 * query that reads `ctx.isDebugInfoEnabled` on the server. (The mutations that
 * read it are not cached.) A new query reading it must be added here.
 */
const HEADER_DEPENDENT_QUERY_KEYS = [
    trpcOptions.complianceAudit.listComplianceAudits.pathKey(),
    trpcOptions.complianceAudit.getComplianceAudit.pathKey(),
    trpcOptions.complianceAudit.getComplianceAuditStep.pathKey(),
];

const headerDependentQueries = {
    predicate: (query) => HEADER_DEPENDENT_QUERY_KEYS.some((queryKey) => matchQuery({ queryKey }, query)),
} satisfies QueryFilters;

/**
 * CUR-1679 — keeps cached query results in step with the `x-debug-info` header.
 *
 * Debug-only response fields (the compliance-audit fallback marks) are decided
 * by the header the request was SENT with, and the header is not part of any
 * query key. Whenever the header's answer changes, every cached result of a
 * query that reads it was fetched under the old one, and so was every such
 * request still in flight: those are cancelled and invalidated, so active ones
 * refetch with the new header at once and inactive ones on their next use.
 * Every other query is left alone — no refetch, and no cancelled request, so an
 * unrelated imperative read (a download URL, say) is never rejected under its
 * caller.
 *
 * The answer can change without anyone touching the debug panel. On a hard
 * load with the toggle persisted on, the first requests go out before PostHog
 * resolves `enable-debug-info` and carry no header; the flag landing later
 * reveals the debug UI over responses that lack its fields. So this watches
 * every input of {@link readIsUIDebugInfoEnabledSync} — the toggle, the flag
 * and PostHog readiness (the reader answers the bootstrap value until then) —
 * rather than the toggle's click handler.
 *
 * Mounted once, in `AppProvider`.
 */
export function useSyncQueriesWithUIDebugInfo(): void {
    const queryClient = useQueryClient();
    // Subscriptions only: each re-renders this hook when one input of the
    // header predicate changes; the predicate itself is read below.
    useDebugStore((state) => state.isUIDebugInfoEnabledFlag);
    useFeatureFlag(UI_DEBUG_INFO_FLAG_KEY, false);
    useSyncExternalStore(subscribeFeatureFlagReadiness, getFeatureFlagReadiness, getFeatureFlagReadiness);

    const isHeaderSent = readIsUIDebugInfoEnabledSync();
    const fetchedWithHeader = useRef(isHeaderSent);

    useEffect(() => {
        if (fetchedWithHeader.current === isHeaderSent) return;
        fetchedWithHeader.current = isHeaderSent;
        // A request still in flight went out under the old header, and invalidating
        // alone would hand its response back: a query with no data yet joins the
        // fetch it is running instead of starting another. So cancel first — each
        // query reverts to its state before that fetch and the late response is
        // dropped — then invalidate, and the refetch reads the header as it is now.
        // A later change cancels whatever this pair started, so the last change wins.
        void queryClient
            .cancelQueries(headerDependentQueries)
            .then(() => queryClient.invalidateQueries(headerDependentQueries));
    }, [isHeaderSent, queryClient]);
}
